/**
 * 「生效区间」= **默认区间对齐到就绪右端**（2026-10-05 第十七批）。
 *
 * 用户确认的口径：「跨平台视图默认 D-2 … 至少是 **N 个整天 对应 N 个整天**」。
 *
 * 路由层（`validateSearch`）是**同步**的，首帧拿不到 `/v1/data-readiness`；
 * 因此那里的 `from`/`to` 只能按「不含今天」算出一个确定性兜底。等就绪度返回后，
 * 页面用本函数把「最近 N 天」重新解析成 **`[readyThrough-(N-1), readyThrough]`**：
 *
 *  · 与「不含今天」**不是两套规则**：同一公式 `resolveDateRange(preset, now, referenceEnd)`，
 *    只是把参考右端从"昨天"换成"就绪右端"（`readyThrough` 通常就是 D-1 或 D-2）；
 *  · 只作用于 `last_days`（最近 N 天）档位；`today` / `yesterday` / `this_month` /
 *    `last_month` / `month` / `custom` 一律原样返回——用户主动选"今天/昨天"就是要看真实
 *    今天/昨天（此时页面用提示条标注"仍在结算"，不偷偷改区间）。
 *  · `readyThroughDate === null`（无法确认）→ 也走 `resolveDateRange`，
 *    参考右端回落到"昨天"，**逐字等于改动前的「不含今天」默认**（不伪造一个就绪日）。
 *
 * 同文件还给「仍在结算」提示条的**触发条件与平台清单**（`settlementFlag`）——纯函数，
 * 与渲染分开，便于单测。
 */

import type { SyncPlatform } from "@ams/contracts";
import { isValidIsoDate, parseDateRangeKey, resolveDateRange, type RangeReferenceEnd } from "./dateRange.js";

export interface EffectiveRange {
  readonly from: string;
  readonly to: string;
  /** 是否真的用就绪右端改写了区间（`false` = 原样返回）。用于 UI 文案 / 测试。 */
  readonly readinessApplied: boolean;
  /** 用户是否打开了「含未结算」开关（右端切到今天；见 `unsettledWindowNote`）。 */
  readonly includeUnsettled: boolean;
}

/**
 * 「最近 N 天」的生效区间。
 *
 *  · 默认（`includeUnsettled=false`）：右端对齐到**就绪右端**（保守口径，2026-10-05 第十七批）；
 *  · 开关打开（`includeUnsettled=true`）：右端切到**今天**（`opsToday`），
 *    用于「我就想看含未结算的日子」——数字会随结算变化，页面必须醒目警告
 *    （见 `unsettledWindowNote`）。
 *
 * 只有 `last_days` 档位会被改写；`today`/`yesterday`/`custom` 等用户显式指定的区间原样返回
 * （用户选"今天"就是要看今天，不替他改）。
 */
export function effectiveRangeFrom(input: {
  readonly rangeKey: string;
  readonly from: string;
  readonly to: string;
  readonly now: Date;
  readonly readyThroughDate: RangeReferenceEnd;
  /** 「含未结算」开关。缺省 false。 */
  readonly includeUnsettled?: boolean;
  /** 运维时区的今天（`unsettledWindowNote` / 开关打开时的参考右端）。 */
  readonly today?: RangeReferenceEnd;
}): EffectiveRange {
  const includeUnsettled = input.includeUnsettled === true;
  const preset = parseDateRangeKey(input.rangeKey);
  if (preset.kind !== "last_days") {
    return { from: input.from, to: input.to, readinessApplied: false, includeUnsettled };
  }
  const today = isValidIsoDate(input.today ?? "") ? (input.today as string) : null;
  const referenceEnd = includeUnsettled ? (today ?? input.readyThroughDate) : input.readyThroughDate;
  const resolved = resolveDateRange(preset, input.now, referenceEnd);
  if (resolved === null) {
    return { from: input.from, to: input.to, readinessApplied: false, includeUnsettled };
  }
  const applied = isValidIsoDate(input.readyThroughDate ?? "");
  return { from: resolved.from, to: resolved.to, readinessApplied: applied, includeUnsettled };
}

/** 「含未结算」窗口的标签与告警（**纯函数**，所有视图共用同一段文案）。 */
export interface UnsettledWindowNote {
  readonly includeUnsettled: boolean;
  /** 生效窗口右端。 */
  readonly rangeEnd: string;
  readonly readyThroughDate: string | null;
  /** 右端是否就是「今天」（开关打开且 today 已知）。 */
  readonly endsOnToday: boolean;
  /** 是否要出**醒目**的「含未结算」告警。 */
  readonly showsUnsettledWarning: boolean;
  /** 一致的一句话文案（两个状态都有；UI 直接显示）。 */
  readonly label: string;
}

function joinDayList(dates: readonly string[]): string {
  return dates.map((date) => (date.length >= 10 ? date.slice(5) : date)).join("、");
}

/**
 * 由「开关 + 生效窗口 + 就绪度」推出**一致的窗口标注**（默认口径与含未结算口径共用）。
 *
 *  · 默认：`数据齐到 2026-10-03；10-04、10-05 仍在结算`；
 *  · 含未结算：`含未结算：窗口右端=今天（2026-10-05）；数据齐到 2026-10-03；10-04、10-05 仍在结算，数字会随结算变化`。
 *
 * 两种状态下都给出 `rangeEnd` 与「齐到哪天」，因此同一条数据的窗口标注可以逐字断言。
 */
export function unsettledWindowNote(input: {
  readonly includeUnsettled: boolean;
  readonly rangeEnd: string;
  readonly readyThroughDate: string | null;
  readonly opsToday: string | null;
  readonly pendingDates?: readonly string[];
  readonly missingDates?: readonly string[];
}): UnsettledWindowNote {
  const ready = input.readyThroughDate;
  const endsOnToday = input.includeUnsettled && input.opsToday !== null && input.rangeEnd === input.opsToday;
  const pending = input.pendingDates ?? [];
  const missing = input.missingDates ?? [];
  const parts: string[] = [];
  if (input.includeUnsettled) {
    parts.push(
      endsOnToday && input.opsToday !== null
        ? `含未结算：窗口右端=今天（${input.opsToday}）`
        : `含未结算：窗口右端=${input.rangeEnd}`,
    );
  }
  if (ready === null) {
    parts.push("数据齐到哪天：无法确认");
  } else {
    parts.push(`数据齐到 ${ready}`);
    if (pending.length > 0) parts.push(`${joinDayList(pending)} 仍在结算`);
    if (missing.length > 0) parts.push(`${joinDayList(missing)} 已过产出时间但没有数据`);
  }
  if (input.includeUnsettled && ready !== null && input.rangeEnd > ready) {
    parts.push("数字会随结算变化（默认口径不含这些日子）");
  }
  return {
    includeUnsettled: input.includeUnsettled,
    rangeEnd: input.rangeEnd,
    readyThroughDate: ready,
    endsOnToday,
    showsUnsettledWarning: input.includeUnsettled && (ready === null || input.rangeEnd > ready),
    label: parts.join("；"),
  };
}

/** 提示条里要标注"几点齐"的单个平台。 */
export interface SettlingPlatformNote {
  readonly platform: SyncPlatform;
  readonly label: string;
  /** 如 `01:00`（来源见端点响应的 `settleClockSource`）。 */
  readonly settleClock: string;
}

export interface SettlementFlag {
  /**
   * 是否要出「仍在结算」提示条。
   *
   * 触发条件（用户原话）：用户**主动选到今天/昨天（或任何晚于 `readyThroughDate` 的区间）**;
   * 另外 `readyThroughDate === null`（无法确认）也提示——未知不等于已结算。
   */
  readonly showsSettlementWarning: boolean;
  /** 提示条里列出的平台（`readyThroughDate === null` 时列全部）。 */
  readonly settlingNotes: readonly SettlingPlatformNote[];
}

/**
 * 由就绪度判定结果推出「提示条」状态（纯函数，便于单测；hook 只做取数）。
 */
export function settlementFlag(input: {
  readonly readyThroughDate: string | null;
  readonly rangeEnd: string;
  /** 就绪度判定给出的「右端还没轮到」的平台（`readiness.pendingPlatforms`）。 */
  readonly pendingPlatforms: readonly SyncPlatform[];
  readonly platforms: readonly SettlingPlatformNote[];
}): SettlementFlag {
  const showsSettlementWarning = input.readyThroughDate === null || input.rangeEnd > input.readyThroughDate;
  const wanted = new Set(
    input.readyThroughDate === null ? input.platforms.map((note) => note.platform) : input.pendingPlatforms,
  );
  return {
    showsSettlementWarning,
    settlingNotes: input.platforms.filter((note) => wanted.has(note.platform)),
  };
}
